import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Button, Paper, IconButton } from '@mui/material';
import { FiArrowLeft, FiEdit2, FiEye, FiTrash2 } from 'react-icons/fi';
import { colors } from '../theme/modernTheme';
import { trustMastersAPI, whatsappTemplatesAPI, emailTemplatesAPI } from '../services/api';
import { showError, showSuccess } from '../utils/toast';
import TrustMasterView from '../components/TrustMasterView';
import LoadingPage from '../components/LoadingPage';
import PreviewModal from '../components/PreviewModal';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';

/**
 * Trust Master Detail Page
 * Displays full details of a trust master in view mode
 */
export default function TrustMasterDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [trustMaster, setTrustMaster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [whatsappTemplate, setWhatsappTemplate] = useState(null);
  const [emailTemplate, setEmailTemplate] = useState(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // Preview modal state
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [previewEmailTemplate, setPreviewEmailTemplate] = useState(null);
  const [previewFilename, setPreviewFilename] = useState('');

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadTrustMaster();
  }, [id]);

  useEffect(() => {
    if (trustMaster) {
      loadTemplates();
    }
  }, [trustMaster]);

  const loadTrustMaster = async () => {
    try {
      setLoading(true);
      const response = await trustMastersAPI.getById(id);
      setTrustMaster(response.data);
    } catch (err) {
      console.error('Error loading trust master:', err);
      showError('Failed to load trust master details');
      navigate('/trust-masters');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const whatsappTemplateId =
        trustMaster?.whatsappTemplateId || trustMaster?.whatsapp_template_id;
      const emailTemplateId = trustMaster?.emailTemplateId || trustMaster?.email_template_id;

      const promises = [];
      if (whatsappTemplateId) {
        promises.push(whatsappTemplatesAPI.getById(whatsappTemplateId));
      }
      if (emailTemplateId) {
        promises.push(emailTemplatesAPI.getById(emailTemplateId));
      }

      if (promises.length === 0) {
        setWhatsappTemplate(null);
        setEmailTemplate(null);
        return;
      }
      const templateRes = await Promise.all(promises);

      if (whatsappTemplateId) {
        const [whatsappTemplateRes] = templateRes;
        const whatsappData = whatsappTemplateRes?.data?.template;
        setWhatsappTemplate(whatsappData || null);
      }

      if (emailTemplateId) {
        const emailTemplateRes = templateRes.length === 2 ? templateRes[1] : templateRes[0];
        const emailData = emailTemplateRes?.data || emailTemplateRes;
        setEmailTemplate(emailData || null);
      }
    } catch (err) {
      console.error('Error loading templates:', err);
      setWhatsappTemplate(null);
      setEmailTemplate(null);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/trust-masters');
  };

  const handleEdit = () => {
    navigate(`/trust-masters/${id}/edit`);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await trustMastersAPI.delete(id);
      showSuccess('Trust master deleted successfully');
      navigate('/trust-masters');
    } catch (error) {
      console.error('Error deleting trust master:', error);
      showError(error?.error?.message || 'Failed to delete trust master');
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handlePreview = async () => {
    try {
      const response = await trustMastersAPI.previewReceipt(id);

      // Extract HTML content from response (PDF receipt)
      const htmlContent = response.data?.html || response.html || '';

      // Fetch email template HTML if email template is assigned
      let emailTemplateHtml = null;
      if (emailTemplate) {
        emailTemplateHtml = emailTemplate.html_body || emailTemplate.htmlBody;
      }

      setPreviewContent(htmlContent);
      setPreviewEmailTemplate(emailTemplateHtml);
      setPreviewFilename(
        `receipt-preview-${(trustMaster.trustName || trustMaster.trust_name || 'trust')
          .toLowerCase()
          .replace(/\s+/g, '-')}-${Date.now()}.pdf`,
      );
      setPreviewModalOpen(true);
    } catch (error) {
      console.error('Error loading receipt preview:', error);
      showError(error?.error?.message || 'Failed to load receipt preview');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const blobData = await trustMastersAPI.downloadReceiptPdf(id);
      const blob = new Blob([blobData], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = previewFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showError('Failed to download receipt PDF');
    }
  };

  const handlePreviewClose = () => {
    setPreviewModalOpen(false);
    setPreviewContent('');
    setPreviewEmailTemplate(null);
    setPreviewFilename('');
  };

  if (loading) {
    return <LoadingPage />;
  }

  if (!trustMaster) {
    return null;
  }

  return (
    <Box>
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={handleBack}
            sx={{
              color: colors.text.secondary,
              '&:hover': {
                backgroundColor: colors.action.hover,
                color: colors.primary.main,
              },
            }}
          >
            <FiArrowLeft size={24} />
          </IconButton>
          <Typography
            variant="h4"
            sx={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: colors.text.primary,
            }}
          >
            Trust Details
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handlePreview}
            sx={{
              borderRadius: '4px',
              textTransform: 'none',
              fontWeight: 600,
              px: 1.5,
              borderColor: colors.primary.main,
              color: colors.primary.main,
              '&:hover': {
                borderColor: colors.primary.dark,
                backgroundColor: `${colors.primary.main}10`,
              },
            }}
          >
            Preview Receipt
          </Button>
          <Button
            variant="outlined"
            onClick={handleEdit}
            sx={{
              borderRadius: '4px',
              textTransform: 'none',
              fontWeight: 600,
              px: 1.5,
              borderColor: colors.primary.main,
              color: colors.primary.main,
              '&:hover': {
                borderColor: colors.primary.dark,
                backgroundColor: `${colors.primary.main}10`,
              },
            }}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            onClick={handleDeleteClick}
            disabled={!trustMaster.can_delete}
            title={
              trustMaster.can_delete
                ? ''
                : 'Cannot delete - Trust is being used in pledges and transactions'
            }
            sx={{
              borderRadius: '4px',
              textTransform: 'none',
              fontWeight: 600,
              px: 1.5,
              borderColor: colors.error.main,
              color: colors.error.main,
              '&:hover': {
                borderColor: colors.error.dark,
                backgroundColor: `${colors.error.main}10`,
              },
              '&:disabled': {
                borderColor: colors.text.disabled,
                color: colors.text.disabled,
                cursor: 'not-allowed',
              },
            }}
          >
            Delete
          </Button>
        </Box>
      </Box>

      {/* View Content */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: '4px',
          border: `1px solid ${colors.border.main}`,
          backgroundColor: colors.background.paper,
        }}
      >
        <TrustMasterView
          trustMaster={trustMaster}
          whatsappTemplate={whatsappTemplate}
          emailTemplate={emailTemplate}
          templatesLoading={templatesLoading}
        />
      </Paper>

      {/* Preview Modal */}
      <PreviewModal
        open={previewModalOpen}
        onClose={handlePreviewClose}
        content={previewContent}
        emailTemplateHtml={previewEmailTemplate}
        downloadFilename={previewFilename}
        onDownloadPDF={handleDownloadPDF}
        previewType="email"
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Trust Master"
        message={`Are you sure you want to delete "${trustMaster?.trustName || trustMaster?.trust_name}"? This action cannot be undone.`}
        loading={deleting}
      />
    </Box>
  );
}
